import { forwardRef, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { Tooltip } from '@hh.ru/magritte-ui-tooltip/Tooltip';
import { TooltipHoverProps } from '@hh.ru/magritte-ui-tooltip/types';

export const TooltipHover = forwardRef<HTMLDivElement, TooltipHoverProps>(
    ({ activatorRef, children, ignoreIntersections = true, size = 'medium', ...props }, ref): ReactElement | null => {
        const [visible, setVisible] = useState(false);
        const isSmallRef = useRef(false);
        const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

        isSmallRef.current = size === 'small';

        const toggleVisible = useCallback(
            (visible: boolean) => {
                if (visible === true) {
                    if (hideTimerRef.current) {
                        clearTimeout(hideTimerRef.current);
                        hideTimerRef.current = null;
                    }
                    setVisible(true);
                    return;
                }

                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                isSmallRef.current
                    ? setVisible(false)
                    : (hideTimerRef.current = setTimeout(() => setVisible(false), 100));
            },
            [setVisible]
        );

        const tooltipRefCallback = useCallback(
            (node: HTMLDivElement | null) => {
                if (node && !isSmallRef.current) {
                    node.addEventListener('mouseenter', () => toggleVisible(true));
                    node.addEventListener('mouseleave', () => toggleVisible(false));
                }
            },
            [toggleVisible]
        );

        const tooltipRefMulti = useMultipleRefs(tooltipRefCallback, ref);

        useEffect(() => {
            const activator = activatorRef.current;
            if (!activator) {
                return void 0;
            }

            let isTouchEnabled = false;

            const handleTouchStart = () => (isTouchEnabled = true);
            const handleMouseEnter = () => !isTouchEnabled && toggleVisible(true);
            const handleMouseLeave = () => toggleVisible(false);

            activator.addEventListener('touchstart', handleTouchStart);
            activator.addEventListener('mouseenter', handleMouseEnter);
            activator.addEventListener('mouseleave', handleMouseLeave);

            return () => {
                activator.removeEventListener('touchstart', handleTouchStart);
                activator.removeEventListener('mouseenter', handleMouseEnter);
                activator.removeEventListener('mouseleave', handleMouseLeave);
            };
        }, [activatorRef, toggleVisible]);

        return (
            <Tooltip
                {...props}
                size={size}
                onClose={() => setVisible(false)}
                ignoreIntersections={ignoreIntersections}
                visible={visible}
                activatorRef={activatorRef}
                ref={tooltipRefMulti}
            >
                {children}
            </Tooltip>
        );
    }
);

TooltipHover.displayName = 'TooltipHover';
